import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

export const WRAP_TIERS = {
  none: { label: "No wrapping", fee: 0 },
  classic: { label: "Classic Wrapping", fee: 10, note: "Ivory paper, black ribbon", noteFee: 5 },
  signature: { label: "Gold Signature Wrapping", fee: 15, note: "Gold paper, black satin bow, ATW wax seal, handwritten note included", noteFee: 0 },
};

const load = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => load("ida_cart", []));
  const [gift, setGift] = useState(() => load("ida_gift", { tier: "none", note: "", noteAddon: false, isGift: false, deliverOn: "" }));
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    localStorage.setItem("ida_cart", JSON.stringify(items));
  }, [items]);
  useEffect(() => {
    if (!user) return undefined;
    const t = setTimeout(() => {
      api.post("/cart/sync", { items: items.map((i) => ({ id: i.id, name: i.name, variant: i.variant, qty: i.qty, price: i.price, image: i.image })) }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [items, user]);
  useEffect(() => {
    localStorage.setItem("ida_gift", JSON.stringify(gift));
  }, [gift]);

  const add = (product, variant = null, price = null) => {
    const key = variant ? `${product.id}::${variant}` : product.id;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + 1 } : i));
      return [
        ...prev,
        {
          key,
          id: product.id,
          name: product.name,
          category: product.category,
          image: product.image,
          price: price ?? product.price,
          variant,
          qty: 1,
        },
      ];
    });
  };

  const addMany = (entries) =>
    setItems((prev) => {
      let next = [...prev];
      for (const { product, variant = null, price = null } of entries) {
        const key = variant ? `${product.id}::${variant}` : product.id;
        const idx = next.findIndex((i) => i.key === key);
        if (idx >= 0) next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        else
          next.push({ key, id: product.id, name: product.name, category: product.category, image: product.image,
            price: price ?? product.price, variant, qty: 1 });
      }
      return next;
    });

  const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const setQty = (key, qty) =>
    setItems((prev) =>
      qty <= 0 ? prev.filter((i) => i.key !== key) : prev.map((i) => (i.key === key ? { ...i, qty } : i))
    );

  const clear = () => {
    setItems([]);
    setGift({ tier: "none", note: "", noteAddon: false, isGift: false, deliverOn: "" });
  };

  const value = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
    const tier = WRAP_TIERS[gift.tier] || WRAP_TIERS.none;
    const wrapFee = items.length ? tier.fee + (gift.tier === "classic" && gift.noteAddon ? tier.noteFee : 0) : 0;
    return {
      items,
      open,
      setOpen,
      add,
      addMany,
      remove,
      setQty,
      clear,
      gift,
      setGift,
      wrapFee,
      subtotal,
      total: subtotal + wrapFee,
      count: items.reduce((s, i) => s + i.qty, 0),
    };
  }, [items, open, gift]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
